// The state machine of «Notificar mismatch». Its own, not deliveryActionFlow: this one keeps the ROW
// number of a success and answers a duplicate, and neither exists in the nine detail actions'
// machine — which models writes on a `Delivery`, and a row in Amazon's sheet is not one.
// Held above the skeleton (DeliveryLayout), so the success line survives a refetch of the detail.
// No `confirming`: «Notificar mismatch» sends on its own click, straight from idle. No `duplicate`
// either: a tracking already in the sheet is never sent again, and whether it is there is the
// check's answer (useAddressMismatchCheck), not a phase of this flow.
export type AddressMismatchPhase = 'idle' | 'pending' | 'success' | 'error';

export interface AddressMismatchState {
  phase: AddressMismatchPhase;
  errorCode: string | null; // the transport code of a failure, only in error
  row: number | null; // the sheet row the report landed on, only in success
}

// What the host's send resolves to. A duplicate is an OUTCOME, not an error: the tracking is already
// in the sheet and the write was held back. `row` is null when the write happened but Google's reply
// did not say where. No date travels with a duplicate: the sheet's first column is the DELIVERY
// date, and the panel only says which tracking was already reported.
export type AddressMismatchOutcome =
  | { kind: 'appended'; row: number | null }
  | { kind: 'duplicate' };

// What the host's check resolves to: whether the tracking is already in the sheet, read BEFORE any
// click. `reported: false` also stands for «could not tell» (no Google token yet, a failed read) —
// the button stays, and the send checks again before writing.
export interface AddressMismatchCheck {
  reported: boolean;
}

export type AddressMismatchEvent =
  | { type: 'SUBMIT' }
  // The send found the tracking already in the sheet and wrote nothing: back to rest.
  | { type: 'DUPLICATE' }
  | { type: 'SUCCESS'; row: number | null }
  | { type: 'ERROR'; code: string | null }
  // The failure has floated to the shell's notice: back to idle, whose CTA is the one-click retry.
  // Leaving `error` is what lets a second failure with the SAME code be reported again — the
  // notice hook only re-reports when its message changes.
  | { type: 'REPORTED' }
  | { type: 'DISMISS' };

export const initialAddressMismatchState: AddressMismatchState = {
  phase: 'idle',
  errorCode: null,
  row: null,
};

// Guarded transitions; an event invalid for the current phase is a no-op, as in deliveryActionFlow.
export function addressMismatchFlow(
  state: AddressMismatchState,
  event: AddressMismatchEvent,
): AddressMismatchState {
  switch (event.type) {
    case 'SUBMIT':
      return state.phase === 'idle' ? { ...initialAddressMismatchState, phase: 'pending' } : state;
    case 'DUPLICATE':
      return state.phase === 'pending' ? initialAddressMismatchState : state;
    case 'SUCCESS':
      return state.phase === 'pending' ? { ...state, phase: 'success', row: event.row } : state;
    case 'ERROR':
      return state.phase === 'pending'
        ? { ...state, phase: 'error', errorCode: event.code }
        : state;
    case 'REPORTED':
      return state.phase === 'error' ? initialAddressMismatchState : state;
    case 'DISMISS':
      return state.phase === 'success' || state.phase === 'error'
        ? initialAddressMismatchState
        : state;
    default:
      return state;
  }
}
